import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar
} from 'recharts';
import {
  BarChart2, Users, Clock, Download, ChevronLeft, Globe, TrendingUp,
  Coffee, Timer, BrainCircuit, ShieldAlert
} from 'lucide-react';
import { useReportFilters } from './useReportFilters';
import { toast } from 'sonner';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function fmtHrs(minutes) {
  if (!minutes) return '–';
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m}m`;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export default function MonthlyReportPage() {
  const navigate = useNavigate();
  const { timezone } = useReportFilters();

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/reports/monthly-attendance`, {
        params: { month: monthStr }, withCredentials: true
      });
      setData(res.data);
    } catch (e) { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchReport(); }, [monthStr]);

  // Per-staff aggregation (deduplicated by date — MERGE time data for multi-session days)
  const staffSummary = (() => {
    if (!data?.records) return [];
    const map = {};
    const statusPriority = {
      present: 5, late: 4, on_leave: 3, leave: 3, holiday: 2, day_off: 1, absent: 0, not_marked: 0
    };
    // Per-staff per-date: track best status + sum all time fields
    const perStaffDate = {};
    data.records.forEach(r => {
      const id = r.staff_id || 'unknown';
      const d = r.date || '';
      const key = `${id}_${d}`;
      if (!perStaffDate[key]) {
        perStaffDate[key] = { ...r };
      } else {
        // Keep best status, SUM time fields across sessions
        if ((statusPriority[r.status] || 0) > (statusPriority[perStaffDate[key].status] || 0)) {
          perStaffDate[key].status = r.status;
        }
        perStaffDate[key].working_minutes  = (perStaffDate[key].working_minutes  || 0) + (r.working_minutes  || 0);
        perStaffDate[key].break_minutes    = (perStaffDate[key].break_minutes    || 0) + (r.break_minutes    || 0);
        perStaffDate[key].idle_minutes     = (perStaffDate[key].idle_minutes     || 0) + (r.idle_minutes     || 0);
        perStaffDate[key].break_count      = (perStaffDate[key].break_count      || 0) + (r.break_count      || 0);
        perStaffDate[key].force_break_count= (perStaffDate[key].force_break_count|| 0) + (r.force_break_count|| 0);
        perStaffDate[key].idle_level_1_minutes = (perStaffDate[key].idle_level_1_minutes || 0) + (r.idle_level_1_minutes || 0);
        perStaffDate[key].idle_level_2_minutes = (perStaffDate[key].idle_level_2_minutes || 0) + (r.idle_level_2_minutes || 0);
        perStaffDate[key].idle_level_3_minutes = (perStaffDate[key].idle_level_3_minutes || 0) + (r.idle_level_3_minutes || 0);
        perStaffDate[key].idle_level_1_count   = (perStaffDate[key].idle_level_1_count   || 0) + (r.idle_level_1_count   || 0);
        perStaffDate[key].idle_level_2_count   = (perStaffDate[key].idle_level_2_count   || 0) + (r.idle_level_2_count   || 0);
        perStaffDate[key].idle_level_3_count   = (perStaffDate[key].idle_level_3_count   || 0) + (r.idle_level_3_count   || 0);
      }
    });
    // Second pass: aggregate per staff
    Object.values(perStaffDate).forEach(r => {
      const id = r.staff_id || 'unknown';
      if (!map[id]) {
        map[id] = {
          name: r.staff_name || id,
          department: r.department || '',
          present: 0, late: 0, absent: 0, on_leave: 0,
          day_off: 0, total: 0,
          working_minutes: 0,
          break_minutes: 0,
          idle_minutes: 0,
          break_count: 0,
          force_break_count: 0,
          idle_level_1_minutes: 0, idle_level_2_minutes: 0, idle_level_3_minutes: 0,
          idle_level_1_count: 0,   idle_level_2_count: 0,   idle_level_3_count: 0,
        };
      }
      const s = r.status || 'not_marked';
      map[id].total++;
      if (s === 'present') map[id].present++;
      else if (s === 'late') { map[id].present++; map[id].late++; }
      else if (s === 'absent' || s === 'not_marked') map[id].absent++;
      else if (s === 'on_leave' || s === 'leave') map[id].on_leave++;
      else if (s === 'holiday') map[id].on_leave++; // count holiday with leave for monthly summary
      else if (s === 'day_off') map[id].day_off++;

      map[id].working_minutes        += (r.working_minutes  || 0);
      map[id].break_minutes          += (r.break_minutes    || 0);
      map[id].idle_minutes           += (r.idle_minutes     || 0);
      map[id].break_count            += (r.break_count      || 0);
      map[id].force_break_count      += (r.force_break_count|| 0);
      map[id].idle_level_1_minutes   += (r.idle_level_1_minutes || 0);
      map[id].idle_level_2_minutes   += (r.idle_level_2_minutes || 0);
      map[id].idle_level_3_minutes   += (r.idle_level_3_minutes || 0);
      map[id].idle_level_1_count     += (r.idle_level_1_count   || 0);
      map[id].idle_level_2_count     += (r.idle_level_2_count   || 0);
      map[id].idle_level_3_count     += (r.idle_level_3_count   || 0);
    });
    return Object.values(map).sort((a, b) => b.present - a.present);
  })();

  // Daily trend for area chart
  const dayTrend = (() => {
    if (!data?.records) return [];
    const map = {};
    data.records.forEach(r => {
      const d = r.date?.split('-')[2] || '?';
      if (!map[d]) map[d] = { day: d, present: 0, absent: 0, late: 0, leave: 0 };
      const s = r.status || 'absent';
      if (s === 'present' || s === 'late') map[d].present++;
      if (s === 'absent' || s === 'not_marked') map[d].absent++;
      if (s === 'late') map[d].late++;
      if (s === 'on_leave' || s === 'leave' || s === 'holiday') map[d].leave++;
    });
    return Object.values(map).sort((a, b) => parseInt(a.day) - parseInt(b.day));
  })();

  const totPresent = staffSummary.reduce((a, s) => a + s.present, 0);
  const totAbsent  = staffSummary.reduce((a, s) => a + s.absent, 0);
  const totWorkMin = staffSummary.reduce((a, s) => a + s.working_minutes, 0);
  const totBreakMin= staffSummary.reduce((a, s) => a + s.break_minutes, 0);
  const totIdleMin = staffSummary.reduce((a, s) => a + s.idle_minutes, 0);
  const totIdleL1Min = staffSummary.reduce((a, s) => a + s.idle_level_1_minutes, 0);
  const totIdleL2Min = staffSummary.reduce((a, s) => a + s.idle_level_2_minutes, 0);
  const totIdleL3Min = staffSummary.reduce((a, s) => a + s.idle_level_3_minutes, 0);
  const totIdleL1Count = staffSummary.reduce((a, s) => a + s.idle_level_1_count, 0);
  const totIdleL2Count = staffSummary.reduce((a, s) => a + s.idle_level_2_count, 0);
  const totIdleL3Count = staffSummary.reduce((a, s) => a + s.idle_level_3_count, 0);
  const avgAtt = (totPresent + totAbsent) > 0 ? Math.round(totPresent / (totPresent + totAbsent) * 100) : 0;

  // Bar chart: working vs break vs idle per staff
  const timeChart = staffSummary.map(s => ({
    name: s.name.split(' ')[0],
    'Working': Math.round(s.working_minutes / 60 * 10) / 10,
    'Break':   Math.round(s.break_minutes   / 60 * 10) / 10,
    'Idle':    Math.round(s.idle_minutes    / 60 * 10) / 10,
  }));

  const handleCSV = () => {
    const csv = [
      'Staff,Department,Present,Late,Absent,Leave,Day Off,Working Hrs,Break Hrs,Idle Hrs,Idle L1 Hrs,Idle L1 Count,Idle L2 Hrs,Idle L2 Count,Idle L3 Hrs,Idle L3 Count,Breaks,Force Breaks,Attendance%'
    ].concat(staffSummary.map(s => {
      const rate = s.total > 0 ? Math.round(s.present / s.total * 100) : 0;
      return [
        s.name, s.department, s.present, s.late, s.absent, s.on_leave, s.day_off,
        (s.working_minutes/60).toFixed(1), (s.break_minutes/60).toFixed(1),
        (s.idle_minutes/60).toFixed(1),
        (s.idle_level_1_minutes/60).toFixed(1), s.idle_level_1_count,
        (s.idle_level_2_minutes/60).toFixed(1), s.idle_level_2_count,
        (s.idle_level_3_minutes/60).toFixed(1), s.idle_level_3_count,
        s.break_count, s.force_break_count, `${rate}%`
      ].join(',');
    })).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `monthly-report-${monthStr}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6" data-testid="monthly-report-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/admin/reports')} className="p-2 rounded-lg hover:bg-gray-100">
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Monthly Report</h1>
          <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-0.5">
            <Globe className="w-3.5 h-3.5" /><span>Times shown in {timezone}</span>
          </div>
        </div>
      </div>

      {/* Month Picker */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Year</Label>
              <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
                <SelectTrigger className="h-8 text-sm w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[now.getFullYear()-1, now.getFullYear()].map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Month</Label>
              <div className="flex flex-wrap gap-1">
                {MONTHS.map((m, i) => (
                  <button key={i} onClick={() => setMonth(i)}
                    className={`px-3 py-1 text-xs rounded-full border transition-all ${i === month ? 'bg-blue-600 text-white border-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}>
                    {m}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && <div className="text-center py-12 text-gray-400">Loading report...</div>}

      {!loading && data && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {[
              { label: 'Total Staff', value: staffSummary.length, color: 'text-gray-700', bg: 'bg-gray-50', Icon: Users },
              { label: 'Avg Attendance', value: `${avgAtt}%`, color: 'text-indigo-600', bg: 'bg-indigo-50', Icon: TrendingUp },
              { label: 'Total Working', value: fmtHrs(totWorkMin), color: 'text-blue-600', bg: 'bg-blue-50', Icon: Timer },
              { label: 'Total Break', value: fmtHrs(totBreakMin), color: 'text-amber-600', bg: 'bg-amber-50', Icon: Coffee },
              { label: 'Total Idle', value: fmtHrs(totIdleMin), color: 'text-red-500', bg: 'bg-red-50', Icon: BrainCircuit },
            ].map(({ label, value, color, bg, Icon }) => (
              <Card key={label} className="border-0 shadow-sm">
                <CardContent className={`pt-4 pb-4 ${bg} rounded-xl flex items-center gap-3`}>
                  <Icon className={`w-7 h-7 ${color} opacity-80 shrink-0`} />
                  <div>
                    <p className={`text-xl font-bold ${color}`}>{value}</p>
                    <p className="text-xs text-gray-500 leading-tight">{label}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Daily Attendance Trend */}
          {dayTrend.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Daily Attendance — {MONTHS[month]} {year}</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={dayTrend}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="present" stroke="#22c55e" fill="#dcfce7" name="Present" />
                    <Area type="monotone" dataKey="absent"  stroke="#ef4444" fill="#fee2e2" name="Absent" />
                    <Area type="monotone" dataKey="late"    stroke="#f59e0b" fill="#fef3c7" name="Late" />
                    <Area type="monotone" dataKey="leave"   stroke="#a855f7" fill="#f3e8ff" name="Leave" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Time Breakdown Chart */}
          {timeChart.some(t => t['Working'] > 0 || t['Break'] > 0) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Timer className="w-4 h-4 text-blue-500" />
                  Time Breakdown by Staff (hours)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={Math.max(200, timeChart.length * 36)}>
                  <BarChart data={timeChart} layout="vertical" barCategoryGap="25%">
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} unit="h" />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={80} />
                    <Tooltip formatter={(v) => [`${v}h`, '']} />
                    <Legend />
                    <Bar dataKey="Working" fill="#3b82f6" name="Working" radius={[0,3,3,0]} />
                    <Bar dataKey="Break"   fill="#f59e0b" name="Break"   radius={[0,3,3,0]} />
                    <Bar dataKey="Idle"    fill="#ef4444" name="Idle"    radius={[0,3,3,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Staff Breakdown Table */}
          {staffSummary.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-500" />
                    Staff Breakdown — {MONTHS[month]} {year}
                  </CardTitle>
                  <Button size="sm" variant="outline" onClick={handleCSV} data-testid="download-csv-btn">
                    <Download className="w-4 h-4 mr-1" /> CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        {['Staff','Dept','Present','Late','Absent','Leave',
                          'Total Hrs','Break Hrs','Idle Hrs','Breaks','Attendance%'].map(h => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {staffSummary.map((s, i) => {
                        const rate = s.total > 0 ? Math.round(s.present / s.total * 100) : 0;
                        return (
                          <tr key={i} className="hover:bg-blue-50/30 transition-colors">
                            <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">{s.name}</td>
                            <td className="px-4 py-3 text-gray-500 text-xs">{s.department || '–'}</td>

                            {/* Attendance counts */}
                            <td className="px-4 py-3">
                              <Badge className="bg-indigo-100 text-indigo-700 border-0">{s.present}</Badge>
                            </td>
                            <td className="px-4 py-3">
                              <Badge className="bg-amber-100 text-amber-700 border-0">{s.late}</Badge>
                            </td>
                            <td className="px-4 py-3">
                              <Badge className="bg-red-100 text-red-700 border-0">{s.absent}</Badge>
                            </td>
                            <td className="px-4 py-3 text-gray-500 text-xs">{s.on_leave || '–'}</td>

                            {/* Time columns */}
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center gap-1.5">
                                <Timer className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                                <span className="font-semibold text-blue-600">{fmtHrs(s.working_minutes)}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center gap-1.5">
                                <Coffee className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                                <span className="font-semibold text-amber-600">{fmtHrs(s.break_minutes)}</span>
                                {s.force_break_count > 0 && (
                                  <span className="flex items-center gap-0.5 text-[10px] bg-orange-100 text-orange-700 px-1 rounded border border-orange-200">
                                    <ShieldAlert className="w-2.5 h-2.5" />{s.force_break_count}F
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-1.5">
                                  <BrainCircuit className="w-3.5 h-3.5 text-red-400 shrink-0" />
                                  <span className={`font-semibold ${s.idle_minutes > 0 ? 'text-red-500' : 'text-gray-300'}`}>
                                    {s.idle_minutes > 0 ? fmtHrs(s.idle_minutes) : '–'}
                                  </span>
                                </div>
                                {s.idle_minutes > 0 && (
                                  <div className="flex flex-wrap gap-1 pl-5">
                                    {s.idle_level_1_minutes > 0 && (
                                      <span className="text-[10px] px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded font-medium">
                                        L1 {fmtHrs(s.idle_level_1_minutes)}{s.idle_level_1_count > 1 ? ` ×${s.idle_level_1_count}` : ''}
                                      </span>
                                    )}
                                    {s.idle_level_2_minutes > 0 && (
                                      <span className="text-[10px] px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded font-medium">
                                        L2 {fmtHrs(s.idle_level_2_minutes)}{s.idle_level_2_count > 1 ? ` ×${s.idle_level_2_count}` : ''}
                                      </span>
                                    )}
                                    {s.idle_level_3_minutes > 0 && (
                                      <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-medium">
                                        L3 {fmtHrs(s.idle_level_3_minutes)}{s.idle_level_3_count > 1 ? ` ×${s.idle_level_3_count}` : ''}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-500">
                              {s.break_count > 0 ? s.break_count : '–'}
                            </td>

                            {/* Attendance % with progress bar */}
                            <td className="px-4 py-3 min-w-[120px]">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 bg-gray-100 rounded-full h-2">
                                  <div
                                    className={`h-2 rounded-full transition-all ${rate >= 80 ? 'bg-indigo-500' : rate >= 50 ? 'bg-amber-400' : 'bg-red-400'}`}
                                    style={{ width: `${rate}%` }}
                                  />
                                </div>
                                <span className={`text-xs font-bold w-8 text-right ${rate >= 80 ? 'text-indigo-600' : rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                                  {rate}%
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>

                    {/* Totals footer */}
                    {staffSummary.length > 1 && (
                      <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                        <tr className="font-semibold text-gray-700">
                          <td className="px-4 py-3" colSpan={2}>Total</td>
                          <td className="px-4 py-3">{totPresent}</td>
                          <td className="px-4 py-3">{staffSummary.reduce((a,s)=>a+s.late,0)}</td>
                          <td className="px-4 py-3">{totAbsent}</td>
                          <td className="px-4 py-3">{staffSummary.reduce((a,s)=>a+s.on_leave,0)}</td>
                          <td className="px-4 py-3 text-blue-600">{fmtHrs(totWorkMin)}</td>
                          <td className="px-4 py-3 text-amber-600">{fmtHrs(totBreakMin)}</td>
                          <td className="px-4 py-3 text-red-500">
                            <div className="flex flex-col gap-1">
                              <span>{totIdleMin > 0 ? fmtHrs(totIdleMin) : '–'}</span>
                              {totIdleMin > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {totIdleL1Min > 0 && <span className="text-[10px] px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded font-medium">L1 {fmtHrs(totIdleL1Min)} ×{totIdleL1Count}</span>}
                                  {totIdleL2Min > 0 && <span className="text-[10px] px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded font-medium">L2 {fmtHrs(totIdleL2Min)} ×{totIdleL2Count}</span>}
                                  {totIdleL3Min > 0 && <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-medium">L3 {fmtHrs(totIdleL3Min)} ×{totIdleL3Count}</span>}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">{staffSummary.reduce((a,s)=>a+s.break_count,0)}</td>
                          <td className="px-4 py-3 text-indigo-600">{avgAtt}%</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {(!data.records || data.records.length === 0) && (
            <div className="text-center py-16 text-gray-400">No attendance data for {MONTHS[month]} {year}</div>
          )}
        </>
      )}
    </div>
  );
}

